import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Grid,
  Icon,
  Tabs,
  Typography,
} from '@statsbomb/kitbag-components';
import { useQuery, useReactiveVar } from '@apollo/client';
import { mf_Games } from '../../../apollo';
import { GET_GAMES } from '../../../components/Header/getGames';
import { GET_GAME_OVERVIEW, GET_GAME_DRAWN_PENALTIES } from './GetGameOverview';
import { KitbagPageGridHolder } from '../../../components/Layout/Layout.styles';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { PAGE_HEADER_DISPLAY } from '../../../components/PageHeader/PageHeader.constants';
import { OVERVIEW_TABS } from './GameOverview.constants';
import PlayByPlayPanel from './PlayByPlayPanel';
import { resolveDrawnPenaltyPlayer } from './PlaysGrid.dataManipulation';
import TrancheStatus from '../../../components/TrancheStatus/TrancheStatus';
import {
  TRANCHE_MODE,
  getStatusesFromGame,
} from '../../../components/TrancheStatus/TrancheStatus.dataManipulation';
import GameVideoModal from './GameVideoModal';
import {
  TabsWrap,
  EmptyState,
  PlayByPlayCardWrap,
} from './GameOverview.styles';

function GameOverview() {
  // While the team -> game cascade resolves, the Header sets mf_Games to null
  // on team change and only fills it once the new team's games have loaded.
  // Skip the query in that window (a gameless query returns empty fast and
  // would flash an empty table) and treat it as loading instead.
  const selectedGameId = useReactiveVar(mf_Games);
  // notifyOnNetworkStatusChange so `loading` is reported on the refetch that
  // fires when the team/game selection changes (otherwise Apollo keeps the
  // previous game's rows on screen instead of showing the loader). Same option
  // the other filter-driven table pages use (League TeamStats/PlayerStats).
  const { data } = useQuery(GET_GAME_OVERVIEW, {
    skip: !selectedGameId,
    notifyOnNetworkStatusChange: true,
  });

  const { data: drawnPenaltyData } = useQuery(GET_GAME_DRAWN_PENALTIES, {
    skip: !selectedGameId,
  });

  // The selected team's games (same query the Header/Layout runs, so this hits
  // the Apollo cache rather than adding a request). It lets us tell "no game
  // resolved yet" apart from "this team genuinely has no games", so we can show
  // a proper empty state instead of an endless loader. notifyOnNetworkStatus so
  // gamesLoading is true while switching teams (avoids a stale-empty flash when
  // leaving a gameless team).
  const { data: gamesData, loading: gamesLoading } = useQuery(GET_GAMES, {
    notifyOnNetworkStatusChange: true,
    variables: { includeT1Games: true },
  });
  const teamHasNoGames =
    !selectedGameId &&
    !gamesLoading &&
    (gamesData?.games?.edges?.length ?? 0) === 0;

  const [activeTab, setActiveTab] = useState('play-by-play');
  const [videoPlayIds, setVideoPlayIds] = useState([]);
  const [videoOpen, setVideoOpen] = useState(false);

  // Switching team/game reuses this same component instance, so clear the
  // video modal whenever the selected game changes - otherwise a stale modal
  // tied to the previous game carries over.
  useEffect(() => {
    setVideoPlayIds([]);
    setVideoOpen(false);
  }, [selectedGameId]);

  // playId -> "Name1, Name2" for players who drew a penalty on that play.
  // The server already filters to drawnPenalty=true rows, so every returned
  // node is a drawn-penalty player-play.
  const drawnPenaltyByPlayId = useMemo(() => {
    const edges = drawnPenaltyData?.playerPlays?.edges ?? [];
    const map = new Map();
    edges.forEach(({ node }) => {
      const playId = node.play?.id;
      if (!playId) return;
      const playerName = node.player?.knownName || node.player?.name;
      if (!playerName) return;
      const existing = map.get(playId);
      map.set(playId, existing ? `${existing}, ${playerName}` : playerName);
    });
    return map;
  }, [drawnPenaltyData]);

  const plays = useMemo(() => {
    const nodes =
      data?.plays?.edges?.map((e) => e.node).filter((p) => p.drive) ?? [];
    return nodes.map((play) => ({
      ...play,
      drawnPenaltyPlayer: resolveDrawnPenaltyPlayer(
        play,
        drawnPenaltyByPlayId.get(play.id),
      ),
    }));
  }, [data, drawnPenaltyByPlayId]);

  const game = plays[0]?.game ?? null;
  const trancheStatuses = getStatusesFromGame(game);

  // PlayByPlayPanel calls onSelectDrive with a drive-row object whose
  // `plays` array contains play objects with numeric `id` fields.
  const onSelectDrive = (drive) => {
    const ids = (drive.plays ?? []).map((p) => String(p.id));
    setVideoPlayIds(ids);
    setVideoOpen(true);
  };

  // PlayByPlayPanel calls onSelectPlay with a play object that has an `id`.
  const onSelectPlay = (play) => {
    setVideoPlayIds([String(play.id)]);
    setVideoOpen(true);
  };

  return (
    <KitbagPageGridHolder>
      <Grid container={false} page>
        <Grid item xs={12}>
          <PageHeader
            href="/game/overview/:leagues/:seasons/:teams/:games"
            rootPage="Game"
            activePage="Overview"
            titleType={PAGE_HEADER_DISPLAY.GAME}
            game={game}
          >
            {trancheStatuses && (
              <TrancheStatus
                mode={TRANCHE_MODE.TWO}
                statuses={trancheStatuses}
              />
            )}
          </PageHeader>

          <TabsWrap>
            <Tabs
              tabs={OVERVIEW_TABS}
              activeTab={activeTab}
              onTabChange={(e, tab) => setActiveTab(tab.value)}
              testId="game-overview-tabs"
            />
          </TabsWrap>

          {teamHasNoGames ? (
            <PlayByPlayCardWrap>
              <Card>
                <EmptyState>
                  <Icon variant="Info" size="regular" />
                  <Typography variant="headingLarge">
                    No Games Available
                  </Typography>
                  <Typography variant="bodyRegular">
                    No games are available for this team and season.
                  </Typography>
                </EmptyState>
              </Card>
            </PlayByPlayCardWrap>
          ) : (
            <PlayByPlayPanel
              game={game}
              onSelectDrive={onSelectDrive}
              onSelectPlay={onSelectPlay}
            />
          )}
        </Grid>
      </Grid>

      <GameVideoModal
        isOpen={videoOpen}
        onClose={() => setVideoOpen(false)}
        playIds={videoPlayIds}
      />
    </KitbagPageGridHolder>
  );
}

export default GameOverview;
